import { useEffect, useRef, useState } from 'react';
import { Play, Download, Loader2, AlertTriangle } from 'lucide-react';
import { api } from '@/api/client';

/**
 * Play (and save) one call's recording, inside the panel.
 *
 * The audio is pulled through our own API — `GET /v1/ivr/calls/:id/recording` — rather than pointed
 * at the provider's host directly. Three reasons, all of which broke the old plain link:
 *   1. the recording host sends no CORS headers, so a browser cannot fetch it from our page;
 *   2. a cross-origin `download` attribute is ignored, so "Download" only ever opened a tab;
 *   3. the request needs our session, and an <audio src> cannot carry the Authorization header —
 *      so the file is fetched as a blob and played from an object URL.
 *
 * Nothing is fetched until Play is pressed: a list of 50 calls must not pull 50 wav files.
 */
export function CallRecordingPlayer({ callId, compact }: { callId: string | number; compact?: boolean }) {
  const [src, setSrc] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const urlRef = useRef<string | null>(null);

  // The object URL holds the whole file in memory until it is revoked.
  useEffect(() => () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current); }, []);

  const load = async (): Promise<string | null> => {
    if (urlRef.current) return urlRef.current;
    setBusy(true);
    setError(null);
    try {
      const res = await api.get(`/ivr/calls/${callId}/recording`, { responseType: 'blob', timeout: 120_000 });
      const url = URL.createObjectURL(res.data as Blob);
      urlRef.current = url;
      setSrc(url);
      return url;
    } catch (e: any) {
      // The blob interceptor means an error body arrives as a Blob — read it back to get the message.
      let msg = 'Could not load the recording';
      try {
        const blob = e?.response?.data;
        if (blob instanceof Blob) msg = JSON.parse(await blob.text())?.message ?? msg;
        else msg = e?.response?.data?.message ?? e?.message ?? msg;
      } catch { /* keep the default */ }
      setError(msg);
      return null;
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    const url = await load();
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = `call-${callId}.wav`;
    document.body.appendChild(a);
    a.click();
    // Removed on the next tick — revoking in the same frame can beat the browser to the save.
    setTimeout(() => a.remove(), 0);
  };

  if (error) {
    return (
      <span title={error} className="inline-flex items-center gap-1 text-[11px] text-amber-700">
        <AlertTriangle size={11} /> {compact ? 'Unavailable' : error}
      </span>
    );
  }

  if (src) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <audio controls autoPlay src={src} className={compact ? 'h-7 w-[190px]' : 'h-8 w-[260px]'} />
        <button onClick={download} title="Save this recording"
          className="text-xs px-1.5 py-1 border border-slate-200 rounded hover:bg-slate-50">
          <Download size={11} />
        </button>
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1">
      <button onClick={load} disabled={busy} title="Play this call"
        className="text-xs px-2 py-1 border border-emerald-200 bg-emerald-50 text-emerald-700 rounded hover:bg-emerald-100 disabled:opacity-50 inline-flex items-center gap-1">
        {busy ? <Loader2 size={11} className="animate-spin" /> : <Play size={11} />} Play
      </button>
      <button onClick={download} disabled={busy} title="Save this recording"
        className="text-xs px-1.5 py-1 border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-50">
        <Download size={11} />
      </button>
    </span>
  );
}
